import { ApplicationElement, html } from '@crossiety/lit-components';
import { ifDefined } from 'lit/directives/if-defined.js';
import translations from './translations.js';
import style from './style.lit.css';
import '@crossiety/lit-components/components/icon/element.js';
import '@crossiety/lit-components/components/img_placeholder/element.js';
import '../origin/element.js';

/**
 * Page header for group page - the title, should be passed as a child(innerHtml) most likely as an hx tag.
 *
 * @prop favorite - Is group among current users favorites
 * @prop bgImage - Headers background image url
 * @prop image - Groups avatar image (optional)
 * @prop isGroupMember - Condition for rendering More/Less info button and Group info
 * @prop membersCount - Number of members
 * @prop membersUrl - Url to group members page
 * @prop type - Type of the current group (options: open, private, closed)
 * @prop name - Will be derived from the innerText of the element and will be passed as Avatar component's name.
 *
 * @slot default - For group name
 * @slot optional-right - For additional content under the header
 * @slot optional-left - For additional content under the header
 * @slot info - For additional group info
 * @slot origin - For Origin component
 */

export default class GroupItem5 extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    favorite: { type: Boolean },
    bgImage: { type: String },
    image: { type: String },
    isGroupMember: { type: Boolean },
    membersCount: { type: Number },
    membersUrl: { type: String },
    type: { type: String },
    name: { state: true },
    isInfoVisible: { state: true },
  };

  connectedCallback() {
    super.connectedCallback();

    this.isInfoVisible = !this.isGroupMember;
  }

  /** @internal */
  static TYPE_ICON_NAMES = {
    closed: 'lock-keyhole',
    private: 'user-lock',
    open: 'unlock-keyhole',
  };

  /** @internal */
  get typeIcon() {
    return this.constructor.TYPE_ICON_NAMES[this.type];
  }

  /** @internal */
  get typeName() {
    return this.t(`type.${this.type}`);
  }

  /** @internal */
  static translations = translations;

  /** @internal */
  get infoButtonLabel() {
    return this.isInfoVisible ? this.t('infoBtn.less') : this.t('infoBtn.more');
  }

  onSlotChange() {
    this.name = this.innerText;
  }

  handleInfoClick() {
    this.isInfoVisible = !this.isInfoVisible;
  }

  handleToggleFavorite() {
    this.favorite = !this.favorite;
  }

  renderStatus() {
    return html`
      <span class="icon-block">
        <ef-icon name=${this.typeIcon}></ef-icon>
        ${this.typeName}
      </span>
    `;
  }

  renderFavorite() {
    return this.favorite
      ? html`<ef-icon name="star:solid" class="favorite"/></ef-icon>`
      : html`<ef-icon name="star" class="favorite"></ef-icon>`;
  }

  renderGroupMembers() {
    return this.membersUrl
      ? html`
          <a href=${this.membersUrl} class="members">
            ${this.t('members', { count: this.membersCount })}
          </a>
        `
      : html`
          <span class="members">
            ${this.t('members', { count: this.membersCount })}
          </span>
        `;
  }

  renderGroupInfoButton() {
    return html`
      <button
        @click=${this.handleInfoClick}
        class="xs-btn btn-outline-light xs-btn-sm">
        ${this.infoButtonLabel}
      </button>
    `;
  }

  renderTextInfo() {
    return html`
      <div class="info">
        <slot @slotchange=${this.onSlotChange}></slot>
        <ef-grid-list col="0" alignh="center" gap="small">
          ${this.renderStatus()}
          ${this.membersCount ? this.renderGroupMembers() : null}
          <slot name="origin"></slot>
        </ef-grid-list>
        ${this.isGroupMember ? this.renderGroupInfoButton() : null}
      </div>
    `;
  }

  renderBgImage() {
    return html`<img src=${this.bgImage} alt=${ifDefined(this.name)} />`;
  }

  render() {
    return html`
      <header>
        ${this.bgImage ? this.renderBgImage() : null} ${this.renderFavorite()}
        <div class="info-wrapper">
          <ef-img-placeholder
            picture=${ifDefined(this.image)}
            alt=${ifDefined(this.name)}>
          </ef-img-placeholder>
          ${this.renderTextInfo()}
        </div>
      </header>

      <div class="slot-wrapper" @group-favorite=${this.handleToggleFavorite}>
        <slot name="optional-left"></slot>
        <slot name="optional-right"></slot>
      </div>
      <slot name="info" ?hidden=${!this.isInfoVisible}>
        <div>This is a slot for Group info.</div>
      </slot>
    `;
  }
}

GroupItem5.registerTagName('xs-group-item-5');

customElements.get('xs-group-item-5') ||
  customElements.define('xs-group-item-5', GroupItem5);
